import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import { useNotifications } from "../lib/notifications";
import {
  SignOut, ListPlus, Receipt, ShieldCheck, IdentificationCard, EnvelopeSimple,
  Phone, Warning, Wallet, House, Copy, Shield, PencilSimple, ClockClockwise,
  ArrowCounterClockwise, FileText, Trash, Scales, Gift, CaretRight, Fingerprint
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatBRL } from "../lib/api";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import { biometricAvailable, isBiometricOptedIn } from "../lib/biometric";

export default function Profile() {
  const { user, logout, refresh, enableBiometric, disableBiometric } = useAuth();
  const { setOpen } = useAuthModal();
  const { disputesPending } = useNotifications();
  const nav = useNavigate();
  const [strikes, setStrikes] = useState(null);
  // V17.9.8 — biometria (FaceID/TouchID/digital) no app nativo
  const [bio, setBio] = useState({ supported: false, enabled: false, type: null, checked: false });

  useEffect(() => {
    if (!user) return;
    api.get("/me/strikes").then((r) => setStrikes(r.data)).catch(() => {});
    (async () => {
      const { available, biometryType } = await biometricAvailable();
      setBio({ supported: !!available, enabled: isBiometricOptedIn(), type: biometryType || null, checked: true });
    })();
  }, [user]);

  const toggleBiometric = async () => {
    if (bio.enabled) {
      await disableBiometric();
      setBio((s) => ({ ...s, enabled: false }));
      toast.success("Login com biometria desativado.");
      return;
    }
    const r = await enableBiometric();
    if (r.ok) {
      setBio((s) => ({ ...s, enabled: true }));
      toast.success("Login com biometria ativado! Da próxima vez que abrir o app, use Face ID/digital.");
    } else {
      toast.error(r.error || "Não foi possível ativar a biometria.");
    }
  };

  const reloadProfile = async () => {
    try { await refresh?.(); } catch { /* noop */ }
    try { const r = await api.get("/me/strikes"); setStrikes(r.data); } catch { /* noop */ }
  };
  const ptr = usePullToRefresh(reloadProfile);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-10 text-center" data-testid="profile-guest">
        <div className="font-heading font-black text-2xl">Entre na sua conta</div>
        <p className="text-slate-600 mt-2 mb-6">Acesse seus aluguéis, anúncios e mensagens.</p>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="open-auth-modal">
          Entrar ou criar conta
        </button>
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    toast.success("Você saiu.");
    nav("/");
  };

  // Status KYC unificado
  const kycStatus = user.kyc_status || (user.status === "ativo_verificado" ? "approved" : "pending");
  const isPJ = user.doc_type === "cnpj";

  // 4 estados visuais distintos
  const kycBadge = (() => {
    if (kycStatus === "approved" || user.status === "ativo_verificado") {
      return {
        type: "verified",
        label: "Identidade verificada",
        icon: ShieldCheck,
        className: "aj-badge-success aj-badge inline-flex items-center gap-1",
        clickable: false,
      };
    }
    if (kycStatus === "aguardando_validacao" || user.status === "aguardando_validacao") {
      return {
        type: "waiting",
        label: "Aguardando aprovação do cadastro",
        icon: ClockClockwise,
        className: "aj-badge inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200",
        clickable: false,
      };
    }
    if (kycStatus === "rejected") {
      return {
        type: "rejected",
        label: "Documento rejeitado — reenviar",
        icon: ArrowCounterClockwise,
        className: "aj-badge inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200",
        clickable: true,
        onClick: () => nav("/perfil/editar"),
      };
    }
    return {
      type: "pending",
      label: "Completar cadastro",
      icon: Warning,
      className: "aj-badge-orange aj-badge inline-flex items-center gap-1",
      clickable: true,
      onClick: () => setOpen(true),
    };
  })();

  const KycIcon = kycBadge.icon;
  const docLabel = isPJ ? "CNPJ" : "CPF";
  const docMask = isPJ
    ? (v) => v?.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
    : (v) => v?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="profile-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      <div className="aj-card p-5 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#004E98] to-[#FF6B35] text-white flex items-center justify-center font-heading font-black text-2xl overflow-hidden shrink-0">
          {user.avatar_url ? (
            <img src={`${API}/files/${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <span>{(user.display_name || user.name)?.[0]?.toUpperCase() || "U"}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-lg truncate">{user.display_name || user.name}</div>
          <div className="text-sm text-slate-500 truncate">{user.email}</div>
          <div className="mt-1.5">
            {kycBadge.clickable ? (
              <button onClick={kycBadge.onClick} className={kycBadge.className} data-testid={`kyc-badge-${kycBadge.type}`}>
                <KycIcon size={12} weight="fill" /> {kycBadge.label}
              </button>
            ) : (
              <span className={kycBadge.className} data-testid={`kyc-badge-${kycBadge.type}`}>
                <KycIcon size={12} weight="fill" /> {kycBadge.label}
              </span>
            )}
          </div>
          {kycBadge.type === "rejected" && user.kyc_rejection_reason && (
            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="kyc-rejection-reason-display">
              <b>Motivo:</b> {user.kyc_rejection_reason}
            </div>
          )}
          {kycBadge.type === "waiting" && (
            <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="kyc-waiting-note">
              Você receberá um e-mail quando o suporte aprovar (até 24h).
            </div>
          )}
        </div>
        <Link
          to="/perfil/editar"
          className="p-2 rounded-full hover:bg-slate-100 text-[#004E98]"
          title="Editar perfil"
          data-testid="edit-profile-btn"
        >
          <PencilSimple size={20} weight="bold" />
        </Link>
      </div>

      <div className="aj-card p-5 space-y-3 text-sm">
        <div className="flex items-center gap-3">
          <IdentificationCard size={20} weight="bold" className="text-[#004E98]" />
          <span className="text-slate-500 flex-1">{docLabel}</span>
          <span className="font-semibold">{user.cpf ? docMask(user.cpf) : "—"}</span>
        </div>
        {isPJ && user.razao_social && (
          <div className="flex items-center gap-3">
            <FileText size={20} weight="bold" className="text-[#004E98]" />
            <span className="text-slate-500 flex-1">Razão Social</span>
            <span className="font-semibold truncate max-w-[200px]">{user.razao_social}</span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Phone size={20} weight="bold" className="text-[#004E98]" />
          <span className="text-slate-500 flex-1">WhatsApp</span>
          <span className="font-semibold">{user.phone || "—"}</span>
        </div>
        <div className="flex items-center gap-3">
          <EnvelopeSimple size={20} weight="bold" className="text-[#004E98]" />
          <span className="text-slate-500 flex-1">E-mail</span>
          <span className="font-semibold truncate max-w-[200px]">{user.email}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Link to="/meus-anuncios" className="aj-card p-4 flex flex-col gap-1.5" data-testid="link-my-listings">
          <ListPlus size={24} weight="bold" className="text-[#FF6B35]" />
          <div className="font-bold text-sm">Meus Anúncios</div>
        </Link>
        <Link to="/minhas-reservas" className="aj-card p-4 flex flex-col gap-1.5" data-testid="link-my-bookings">
          <Receipt size={24} weight="bold" className="text-[#004E98]" />
          <div className="font-bold text-sm">Meus Aluguéis</div>
        </Link>
        <Link to="/enderecos" className="aj-card p-4 flex flex-col gap-1.5" data-testid="link-addresses">
          <House size={24} weight="bold" className="text-[#16A34A]" />
          <div className="font-bold text-sm">Endereços</div>
          <div className="text-xs text-slate-500">Locais de retirada</div>
        </Link>
        <Link to="/carteira" className="aj-card p-4 flex flex-col gap-1.5" data-testid="link-wallet">
          <Wallet size={24} weight="bold" className="text-[#FF6B35]" />
          <div className="font-bold text-sm">Minha Carteira</div>
          <div className="text-xs text-slate-500">Saldo, saque e antecipação</div>
        </Link>
        <Link to="/perfil/editar?focus=horarios" className="aj-card p-4 flex flex-col gap-1.5" data-testid="link-working-hours">
          <ClockClockwise size={24} weight="bold" className="text-[#004E98]" />
          <div className="font-bold text-sm">Horários de Atendimento</div>
          <div className="text-xs text-slate-500">Dias e horas de entrega/retirada</div>
        </Link>
      </div>

      {/* V14.0: Indique e Ganhe */}
      <Link
        to="/recompensas"
        className="block rounded-2xl p-4 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(120deg, #004E98 0%, #0066CC 70%, #FF6B35 170%)" }}
        data-testid="link-rewards"
      >
        <div className="absolute -right-3 -top-3 opacity-20"><Gift size={72} weight="fill" /></div>
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <Gift size={24} weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-base leading-tight">Indique e Ganhe</div>
            <div className="text-xs text-white/85">Convide amigos e ganhe dinheiro de verdade</div>
          </div>
          <CaretRight size={20} weight="bold" className="shrink-0 opacity-80" />
        </div>
      </Link>

      <ApplyReferralCard />

      {/* V12.8: Strikes do dono (cancelamentos por falta de estoque) */}
      {strikes && (strikes.count > 0 || strikes.pending_debt > 0) && (
        <div className="aj-card p-4 border-2 border-amber-200 bg-amber-50/60 space-y-2" data-testid="profile-strikes-card">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-amber-600" />
            <div className="font-bold text-sm">Cancelamentos por falta de estoque</div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Nos últimos {strikes.reset_months} meses</span>
            <span className="font-bold" data-testid="strikes-count">{strikes.count} de {strikes.free_limit} isentos usados</span>
          </div>
          {strikes.free_remaining > 0 ? (
            <p className="text-xs text-amber-800">
              Você ainda tem <b>{strikes.free_remaining}</b> cancelamento(s) isento(s). A partir daí, há multa de {formatBRL(strikes.multa_next)} por cancelamento.
            </p>
          ) : (
            <p className="text-xs text-red-700">
              Você atingiu o limite isento. Novos cancelamentos por falta de estoque geram multa de {formatBRL(strikes.multa_next)}.
            </p>
          )}
          {strikes.pending_debt > 0 && (
            <div className="flex items-center justify-between text-sm bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5" data-testid="strikes-pending-debt">
              <span className="text-red-700 font-semibold">Multa pendente</span>
              <span className="font-bold text-red-700">{formatBRL(strikes.pending_debt)}</span>
            </div>
          )}
          {strikes.pending_debt > 0 && (
            <p className="text-[11px] text-slate-500">A multa pendente é descontada automaticamente do seu próximo repasse de aluguel.</p>
          )}
          {strikes.reset_at && (
            <p className="text-[11px] text-slate-500">Contador zera em {new Date(strikes.reset_at).toLocaleDateString("pt-BR")}.</p>
          )}
          <p className="text-[11px] text-slate-500">💡 Dica: pause anúncios sem estoque em vez de cancelar reservas já feitas.</p>
        </div>
      )}

      {/* Central de Disputas */}
      <Link to="/disputas" className="aj-card p-4 flex items-center gap-3" data-testid="link-disputes">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
          <Scales size={22} weight="duotone" className="text-[#004E98]" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">Central de Disputas</div>
          <div className="text-xs text-slate-500">Acompanhe e resolva disputas das suas locações</div>
        </div>
        {disputesPending > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow-sm" data-testid="disputes-pending-badge">
            {disputesPending > 99 ? "99+" : disputesPending}
          </span>
        )}
        <span className="text-slate-400">›</span>
      </Link>

      {/* Termos, Tarifas e Políticas */}
      <Link to="/termos" className="aj-card p-4 flex items-center gap-3" data-testid="link-terms">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
          <FileText size={22} weight="duotone" className="text-slate-700" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">Termos, Tarifas e Políticas</div>
          <div className="text-xs text-slate-500">Taxas, regras de uso e responsabilidades</div>
        </div>
        <span className="text-slate-400">›</span>
      </Link>

      {/* Privacidade e exclusão de conta (LGPD / Google Play) */}
      <Link to="/exclusao-de-conta" className="aj-card p-4 flex items-center gap-3" data-testid="link-data-deletion">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
          <Trash size={22} weight="duotone" className="text-slate-700" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">Privacidade e exclusão de conta</div>
          <div className="text-xs text-slate-500">Como seus dados são tratados e como excluir a conta</div>
        </div>
        <span className="text-slate-400">›</span>
      </Link>

      {/* V18.9.18 — Diagnóstico de localização desativado (já validado em produção).
          O componente segue em components/GeoDiagnosticsCard.jsx caso precise voltar. */}

      {/* V17.9.8 — Login com biometria (FaceID / TouchID / digital) — só aparece no app nativo */}
      {bio.checked && bio.supported && (
        <div className="aj-card p-4 flex items-center gap-3" data-testid="profile-biometric-card">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Fingerprint size={22} weight="bold" className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Entrar com {bio.type === "FACE_ID" ? "Face ID" : bio.type === "TOUCH_ID" ? "Touch ID" : "biometria"}</div>
            <div className="text-xs text-slate-500">
              {bio.enabled
                ? "Ativado — no próximo login, use sua biometria para abrir a conta rapidamente."
                : "Ative para desbloquear sua conta sem digitar a senha toda vez."}
            </div>
          </div>
          <button
            type="button"
            onClick={toggleBiometric}
            role="switch"
            aria-checked={bio.enabled}
            className={`shrink-0 relative w-12 h-7 rounded-full transition ${bio.enabled ? "bg-emerald-500" : "bg-slate-300"}`}
            data-testid="biometric-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition ${bio.enabled ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>
      )}

      <button onClick={handleLogout} className="btn-ghost w-full" data-testid="logout-btn">
        <SignOut size={18} weight="bold" /> Sair
      </button>

      {user.role === "admin" && (
        <Link
          to="/admin"
          className="aj-card p-4 flex items-center gap-3 border-2 border-[#FF6B35] bg-[#FF6B35]/5"
          data-testid="link-admin-panel"
        >
          <Shield size={22} weight="fill" className="text-[#FF6B35]" />
          <div className="flex-1">
            <div className="font-bold text-sm">Painel de Suporte</div>
            <div className="text-xs text-slate-500">Disputas, moderação e métricas</div>
          </div>
        </Link>
      )}

      {/* Support ID - footer */}
      <div className="text-center pt-4 border-t border-slate-200">
        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">
          ID de Suporte
        </div>
        <button
          onClick={() => {
            if (user.support_id) {
              navigator.clipboard.writeText(user.support_id);
              toast.success(`ID ${user.support_id} copiado!`);
            }
          }}
          className="inline-flex items-center gap-2 text-sm font-mono font-bold text-slate-600 hover:text-[#004E98]"
          data-testid="support-id-display"
        >
          {user.support_id || "—"}
          <Copy size={14} weight="bold" />
        </button>
        <div className="text-[10px] text-slate-400 mt-1">
          Use este ID ao contatar o suporte para resolver mais rápido.
        </div>
      </div>
    </div>
  );
}


function ApplyReferralCard() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null); // { can_apply, already, within_window }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/referrals/can-apply").then((r) => setState(r.data)).catch(() => {});
  }, [user]);

  if (!state || !state.can_apply || done) return null;

  const apply = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { toast.error("Digite um código."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/referrals/apply-code", { code: c });
      toast.success(`Código aplicado! Você foi indicado por ${data.referrer_name}. 🎁`);
      setDone(true);
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível aplicar o código.");
    } finally { setBusy(false); }
  };

  return (
    <div className="aj-card p-4 border-2 border-[#FF6B35]/30 bg-[#FF6B35]/[0.04] space-y-2" data-testid="apply-referral-card">
      <div className="flex items-center gap-2">
        <Gift size={20} weight="fill" className="text-[#FF6B35]" />
        <div className="font-bold text-sm">Alguém te indicou?</div>
      </div>
      <p className="text-xs text-slate-500">Insira o código de indicação (até 7 dias após criar a conta).</p>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 16))}
          placeholder="Ex: AJ1234" className="aj-input flex-1 uppercase" data-testid="apply-referral-input" />
        <button onClick={apply} disabled={busy} className="px-4 rounded-xl bg-[#FF6B35] text-white font-bold disabled:opacity-60" data-testid="apply-referral-btn">
          {busy ? "..." : "Aplicar"}
        </button>
      </div>
    </div>
  );
}
